import { CobaltButton } from '@cae/cobalt-react/components/button';
import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import { CobaltIconEditOutline } from '@cae/cobalt-react/icons/outline/CobaltIconEditOutline';
import { useHasPermission } from '@contexts/permissions-provider';
import { PrecheckinWidget } from '@features/access-passes/trainee-access-pass/precheckin-widget/PrecheckinWidget';
import { useProfileNavigation } from '@features/profile/hooks/use-profile-navigation/useProfileNavigation';
import { formatCountryName } from '@features/profile/utils/format-country-name/formatCountryName';
import { formatPhoneNumber } from '@features/profile/utils/format-phone-number/formatPhoneNumber';
import { normalizeDateToLocalDateOnly } from '@features/profile/utils/normalize-date/normalizeDate';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import { format } from 'date-fns';
import * as React from 'react';

import { useNationalities } from '../../hooks/use-nationalities/useNationalities';
import { useProfile } from '../../hooks/use-profile/useProfile';
import styles from './PersonalInformationForm.module.css';
import { formatOptional, NOT_SPECIFIED } from './PersonalInformationForm.utils';

const PersonalInformationForm = React.memo((): React.ReactElement => {
  const { profile, isProfileError, isProfileLoading } = useProfile();
  const isMobile = useMediaQuery(MediaQuery.Mobile);
  const hasTrainingPermission = useHasPermission(
    'ProfilePersonalTraining_FullAccess_Own'
  );
  const { nationalities } = useNationalities(hasTrainingPermission);
  const citizenshipDisplay = React.useMemo(() => {
    const citizenship = profile?.lastGrantedCitizenship?.trim();
    if (!citizenship) return NOT_SPECIFIED;
    const nationality = nationalities.find(n => n.iso2Code === citizenship);
    return formatOptional(nationality?.name || citizenship);
  }, [profile?.lastGrantedCitizenship, nationalities]);
  const {
    navigateToEditIdentity,
    navigateToEditContact,
    navigateToEditCitizenship,
    navigateToEditOtherInformation,
  } = useProfileNavigation();

  const birthdateDisplay = React.useMemo(() => {
    const date = normalizeDateToLocalDateOnly(profile?.birthdate);
    return date ? format(date, 'dd-MM-yyyy') : NOT_SPECIFIED;
  }, [profile?.birthdate]);

  if (isProfileError) {
    return <InlineFeedback type="data-error" />;
  }

  if (isProfileLoading) {
    return <ContentLoader />;
  }

  return (
    <div data-heap-id="personal-information-form" className={styles.wrapper}>
      <div className={styles.scrollArea}>
        {!isMobile && <div className="heading-02">Personal information</div>}
        <PrecheckinWidget isMessageWidget={true} />
        <div className={styles.form}>
          <div className={styles.section}>
            <div className={styles.title}>
              <div className="heading-01">Identity information</div>
              <CobaltButton
                data-heap-id="profile-personal-info-identity-edit-btn"
                variant="ghost"
                size="small"
                onClick={navigateToEditIdentity}
              >
                <CobaltIconEditOutline />
                Edit
              </CobaltButton>
            </div>
            <div className={styles.content}>
              <div className={styles.item}>
                <div className={`body-01 ${styles.label}`}>Salutation</div>
                <div
                  className={`body-02 ${styles.value}`}
                  data-empty={!profile?.salutation?.trim()}
                >
                  {formatOptional(profile?.salutation)}
                </div>
              </div>
              <div className={styles.item}>
                <div className={`body-01 ${styles.label}`}>First name</div>
                <div
                  className={`body-02 ${styles.value}`}
                  data-empty={!profile?.givenName?.trim()}
                >
                  {formatOptional(profile?.givenName)}
                </div>
              </div>
              <div className={styles.item}>
                <div className={`body-01 ${styles.label}`}>Middle name</div>
                <div
                  className={`body-02 ${styles.value}`}
                  data-empty={!profile?.middleName?.trim()}
                >
                  {formatOptional(profile?.middleName)}
                </div>
              </div>
              <div className={styles.item}>
                <div className={`body-01 ${styles.label}`}>Last name</div>
                <div
                  className={`body-02 ${styles.value}`}
                  data-empty={!profile?.lastName?.trim()}
                >
                  {formatOptional(profile?.lastName)}
                </div>
              </div>
              <div className={styles.item}>
                <div className={`body-01 ${styles.label}`}>Date of birth</div>
                <div
                  className={`body-02 ${styles.value}`}
                  data-empty={!profile?.birthdate}
                >
                  {birthdateDisplay}
                </div>
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.title}>
              <div className="heading-01">Contact information</div>
              <CobaltButton
                data-heap-id="profile-personal-info-contact-edit-btn"
                variant="ghost"
                size="small"
                onClick={navigateToEditContact}
              >
                <CobaltIconEditOutline />
                Edit
              </CobaltButton>
            </div>
            <div className={styles.content}>
              <div className={styles.item}>
                <div className={`body-01 ${styles.label}`}>Email</div>
                <div
                  className={`body-02 ${styles.value}`}
                  data-empty={!profile?.email?.trim()}
                >
                  {formatOptional(profile?.email)}
                </div>
              </div>
              <div className={styles.item}>
                <div className={`body-01 ${styles.label}`}>
                  Mobile phone number
                </div>
                <div
                  className={`body-02 ${styles.value}`}
                  data-empty={!profile?.mobileNumber?.trim()}
                >
                  {profile?.mobileNumber?.trim()
                    ? formatPhoneNumber(profile.mobileNumber.trim())
                    : NOT_SPECIFIED}
                </div>
              </div>
            </div>
          </div>
          {hasTrainingPermission && (
            <div className={styles.section}>
              <div className={styles.title}>
                <div className="heading-01">Citizenship and residency</div>
                <CobaltButton
                  data-heap-id="profile-personal-info-citizenship-edit-btn"
                  variant="ghost"
                  size="small"
                  onClick={navigateToEditCitizenship}
                >
                  <CobaltIconEditOutline />
                  Edit
                </CobaltButton>
              </div>
              <div className={styles.content}>
                <div className={styles.item}>
                  <div className={`body-01 ${styles.label}`}>
                    Last granted citizenship
                  </div>
                  <div
                    className={`body-02 ${styles.value}`}
                    data-empty={!profile?.lastGrantedCitizenship?.trim()}
                  >
                    {citizenshipDisplay}
                  </div>
                </div>
                <div className={styles.item}>
                  <div className={`body-01 ${styles.label}`}>
                    Country of permanent residency
                  </div>
                  <div
                    className={`body-02 ${styles.value}`}
                    data-empty={!profile?.currentPermanentResidency?.trim()}
                  >
                    {profile?.currentPermanentResidency?.trim()
                      ? formatCountryName(
                          profile.currentPermanentResidency.trim()
                        )
                      : NOT_SPECIFIED}
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className={styles.section}>
            <div className={styles.title}>
              <div className="heading-01">Other personal information</div>
              <CobaltButton
                data-heap-id="profile-personal-info-other-info-edit-btn"
                variant="ghost"
                size="small"
                onClick={navigateToEditOtherInformation}
              >
                <CobaltIconEditOutline />
                Edit
              </CobaltButton>
            </div>
            <div className={styles.content}>
              <div className={styles.item}>
                <div className={`body-01 ${styles.label}`}>Preferred name</div>
                <div
                  className={`body-02 ${styles.value}`}
                  data-empty={!profile?.nickName?.trim()}
                >
                  {formatOptional(profile?.nickName)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

PersonalInformationForm.displayName = 'PersonalInformationForm';

export { PersonalInformationForm };
